import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Copy, Upload } from "lucide-react";
import { Button } from "@/components/ui/button";
import { api, formatErr, previewUrl, rupiah } from "@/lib/api";
import { PageHeader, StatusBadge } from "@/components/Common";

const OrderCard = ({ order, onUpdated }) => {
  const ref = useRef();
  const [busy, setBusy] = useState(false);
  const pg = order.photographer;
  const canUpload = ["pending_payment", "rejected", "waiting_confirmation"].includes(order.status);

  const upload = async (file) => {
    if (!file) return;
    setBusy(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      onUpdated((await api.post(`/orders/${order.id}/proof`, fd)).data);
      toast.success("Bukti transfer terkirim. Menunggu konfirmasi fotografer.");
    } catch (e) {
      toast.error(formatErr(e));
    } finally {
      setBusy(false);
      ref.current.value = "";
    }
  };

  return (
    <div className="rounded-2xl bg-white border border-zinc-200 p-6" data-testid={`order-card-${order.id}`}>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <p className="font-mono-x text-sm text-zinc-500">{order.code}</p>
          <p className="font-bold text-lg">{pg.name}</p>
          {order.bundle && <p className="text-xs font-semibold text-blue-700" data-testid={`order-bundle-${order.id}`}>Paket hemat · {order.bundle.event_title} · hemat {rupiah(order.bundle.regular_total - order.total)}</p>}
          {order.items.some((i) => i.bonus) && <p className="text-xs font-semibold text-emerald-700" data-testid={`order-bonus-${order.id}`}>+{order.items.filter((i) => i.bonus).length} foto baru otomatis masuk paket (gratis)</p>}
          {order.promo_code && <p className="text-xs font-semibold text-emerald-700" data-testid={`order-promo-${order.id}`}>Promo {order.promo_code} · -{rupiah(order.discount)}</p>}
          {order.packages_applied?.length > 0 && <p className="text-xs font-semibold text-blue-700" data-testid={`order-packages-${order.id}`}>{order.packages_applied.map((a) => `Paket ${a.count} foto`).join(" + ")} · hemat {rupiah(order.regular_total - order.subtotal)}</p>}
        </div>
        <StatusBadge status={order.status} testId={`order-status-${order.id}`} />
      </div>
      <div className="flex gap-2 mt-4 overflow-x-auto">
        {order.items.map((i) => <img key={i.photo_id} src={previewUrl(i.photo_id)} alt="" className="h-16 w-24 rounded-lg object-cover shrink-0" />)}
      </div>
      {order.status === "rejected" && order.note && <p className="mt-4 text-sm text-rose-600" data-testid={`order-note-${order.id}`}>Alasan ditolak: {order.note}</p>}
      <div className="mt-5 grid sm:grid-cols-2 gap-4 items-end">
        <div className="rounded-xl bg-zinc-50 border border-zinc-200 p-4 text-sm">
          <p className="text-xs uppercase tracking-widest text-zinc-400 font-bold mb-2">Transfer ke</p>
          {pg.bank_account ? (
            <>
              <p className="font-semibold">{pg.bank_name} · {pg.bank_holder}</p>
              <button className="font-mono-x text-base inline-flex items-center gap-2 hover:text-blue-600" data-testid={`order-copy-account-${order.id}`}
                onClick={() => { navigator.clipboard?.writeText(pg.bank_account); toast.success("Nomor rekening disalin"); }}>
                {pg.bank_account} <Copy className="h-3.5 w-3.5" />
              </button>
            </>
          ) : <p className="text-zinc-500">Fotografer belum mengisi rekening.</p>}
        </div>
        <div className="sm:text-right">
          <p className="text-sm text-zinc-500">Total</p>
          <p className="font-display text-2xl font-extrabold" data-testid={`order-total-${order.id}`}>{rupiah(order.total)}</p>
          {canUpload && (
            <Button onClick={() => ref.current.click()} disabled={busy} variant={order.status === "waiting_confirmation" ? "outline" : "default"}
              className={`mt-3 rounded-xl ${order.status === "waiting_confirmation" ? "" : "bg-blue-600 hover:bg-blue-700"}`} data-testid={`order-upload-proof-${order.id}`}>
              <Upload className="mr-2 h-4 w-4" />{busy ? "Mengunggah..." : order.has_proof ? "Ganti bukti transfer" : "Unggah bukti transfer"}
            </Button>
          )}
          <input ref={ref} type="file" accept="image/jpeg,image/png,image/webp" className="hidden" onChange={(e) => upload(e.target.files[0])} data-testid={`order-proof-input-${order.id}`} />
        </div>
      </div>
    </div>
  );
};

export default function OrdersPage() {
  const [orders, setOrders] = useState(null);
  useEffect(() => { api.get("/orders/my").then((r) => setOrders(r.data)); }, []);
  const update = (o) => setOrders((prev) => prev.map((x) => (x.id === o.id ? o : x)));

  return (
    <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-14">
      <PageHeader label="Pembayaran manual" title="Pesanan Saya" />
      {orders === null ? <p className="text-zinc-500">Memuat...</p> : orders.length === 0 ? (
        <p className="text-zinc-500" data-testid="orders-empty">Belum ada pesanan.</p>
      ) : (
        <div className="space-y-5">{orders.map((o) => <OrderCard key={o.id} order={o} onUpdated={update} />)}</div>
      )}
    </main>
  );
}
